// ─────────────────────────────────────────────────────────────────────────────
// mackCalculatorView.js — THE READ-ONLY VIEW MODEL for Mack's Calculator decision interface.
//
// FRONT END ONLY (owner, 2026-10-14): "what you do with the information, not how it's got."
// This module starts AFTER the response has arrived (GET /api/mack/calculator, decoded by
// lib/mackCalculatorEvidence.ts exactly as before) and never fetches, writes or computes a
// score. It only GROUPS, COUNTS and FORMATS what the backend sent:
//
//   STATE_LANGUAGE   every user-facing state label comes from the backend's own code through
//                    this ONE table (the spec's STATE LANGUAGE table). A code that is not in it
//                    shows AS ITSELF — never as "Current". "Update overdue" has no entry: no
//                    backend code for a missed window has been seen, and the front end never
//                    works it out from dates.
//   DESIGN_ROWS      picture 2's two tables (the original design), matched to the response's
//                    criteria by IDENTITY (criterion_key). The response carries `block`, and its
//                    `families` order; it does not carry the macro/sentiment split, which comes
//                    from these rows. A criterion matching no row is UNASSIGNED, never forced.
//   LEDGER           every response field, and where it is shown. `otherFields` returns every key
//                    the ledger does not name, so a field the backend adds later is never dropped.
//
// PLAIN JS, no imports: the Trading web and iOS previews carry this file byte for byte.
// ─────────────────────────────────────────────────────────────────────────────

const own = (obj, key) => obj != null && key != null && Object.prototype.hasOwnProperty.call(obj, key);

// ── STATE LANGUAGE (spec table → the backend codes that carry each meaning) ──
// The meaning column is the spec's; the codes are the response's, each matched by the backend's
// own `reason` / `next_action` text (recorded in STATE_EVIDENCE for the ledger).
export const STATE_LANGUAGE = {
  current: 'Current',
  CURRENT: 'Current',
  SCHEDULED: 'Next update scheduled',
  UPDATE_PENDING: 'Awaiting source update',
  HISTORY_ONLY: 'History available',
  SOURCE_UNAVAILABLE: 'Source unavailable',
  PROVIDER_ACCESS_REQUIRED: 'Provider access required',
  NO_RETAINED_OCCURRENCE: 'Awaiting upstream evidence',
  FORMULA_PENDING_INPUT_UNAVAILABLE: 'Awaiting upstream evidence',
  FORMULA_PASS2_PENDING: 'Awaiting Formula Pass 2',
  NO_MATCHING_ARTICLES_IN_DECLARED_WINDOW: 'No matching evidence in this window',
};
export const STATE_EVIDENCE = {
  current: 'Current and within expected source lifecycle — reason "Latest admitted source observation; no newer expected release is proved due"',
  CURRENT: 'freshness_evidence.state for the same observations as `current`',
  SCHEDULED: 'Waiting for the next declared source release — Calendar trigger with a scheduled occurrence',
  UPDATE_PENDING: 'Source has not published a new observation yet — reason UPSTREAM_STATE_UPDATE_PENDING',
  HISTORY_ONLY: 'Only retained history is available — reason UPSTREAM_STATE_HISTORY_ONLY',
  SOURCE_UNAVAILABLE: 'Exact public route unavailable — reason UPSTREAM_STATE_SOURCE_UNAVAILABLE',
  PROVIDER_ACCESS_REQUIRED: 'Licensed or credentialed access required — next_action "Provision licensed provider access"',
  NO_RETAINED_OCCURRENCE: 'Required upstream evidence missing — the Calendar holds no retained occurrence',
  FORMULA_PENDING_INPUT_UNAVAILABLE: 'Required upstream evidence missing — the criterion names its blocking_evidence_ids',
  FORMULA_PASS2_PENDING: 'Formula output not yet implemented',
  NO_MATCHING_ARTICLES_IN_DECLARED_WINDOW: 'No matching declared-window evidence — reason "No matching retained article metadata in the declared window"',
};
export function stateLabel(code) {
  if (code === null || code === undefined || code === '') return '—';
  return own(STATE_LANGUAGE, code) ? STATE_LANGUAGE[code] : String(code);
}

// Readiness and gap sets — the backend's own codes, never a date or a value.
export const READY_FORMULA_STATES = ['FORMULA_PASS2_PENDING'];
export const WAITING_FORMULA_STATES = ['FORMULA_PENDING_INPUT_UNAVAILABLE'];
export const ACCESS_GAP_STATES = ['SOURCE_UNAVAILABLE', 'PROVIDER_ACCESS_REQUIRED'];

// The response's flags, in words (same rule: the table speaks, the front end does not decide).
export const FLAG_LANGUAGE = {
  scores_enabled: { false: 'Downstream calculations disabled', true: 'Downstream calculations enabled' },
  formula_execution: { false: 'No formula ran', true: 'Formula execution on' },
};
export function flagLabel(name, value) {
  const t = own(FLAG_LANGUAGE, name) ? FLAG_LANGUAGE[name] : null;
  const k = String(value);
  return t && own(t, k) ? t[k] : `${name}: ${k}`;
}

// ── THE ORIGINAL DESIGN'S ROWS (picture 2) ──────────────────────────────────
// [criterion_key in the response, the design's row name, the design's block]
export const DESIGN_ROWS = {
  macro: [
    ['policy_rate_level', 'Interest Rate Level', 'Policy'],
    ['policy_rate_trend', 'Interest Rate Trend', 'Policy'],
    ['inflation_yoy', 'Inflation YoY', 'Prices'],
    ['gdp_growth_trend', 'GDP Growth Trend', 'Growth'],
    ['trade_balance', 'Trade Balance', 'External'],
    ['capital_flow_attraction', 'Capital Flow Attraction', 'External'],
    ['cot_bias', 'COT Positioning Bias', 'Positioning'],
  ],
  sentiment: [
    ['equity_signal', 'Equity Sentiment', 'Risk'],
    ['bond_yield_signal', 'Bond Yield Sentiment', 'Positioning'],
    ['volatility_signal', 'Volatility VIX/OVX', 'Risk'],
    ['retail_inverted', 'Retail Positioning', 'Positioning'],
    ['cot_reversal', 'COT Reversal', 'Positioning'],
    ['global_risk_signal', 'Global Risk', 'Risk'],
    ['news_sentiment_signal', 'News Sentiment', 'Risk'],
  ],
};
export const BLOCKS = ['Policy', 'Prices', 'Growth', 'External', 'Positioning', 'Risk'];
// The original design's five environments (names only — read-only, never clickable).
export const ENVIRONMENTS = ['Stable', 'Inflation Wk', 'Reversal', 'Panic', 'Euphoria'];
// The original design's three bottom tiles and their pending words.
export const TILES = [
  { key: 'strongest_block', label: 'Strongest block', pending: 'Insufficient governed inputs' },
  { key: 'horizon', label: 'Horizon it implies', pending: 'Awaiting Formula Pass 2' },
  { key: 'weighted_composite', label: 'Weighted composite', pending: 'Awaiting Formula Pass 2' },
];

export function designRowFor(criterionKey) {
  for (const group of ['macro', 'sentiment']) {
    const row = DESIGN_ROWS[group].find((r) => r[0] === criterionKey);
    if (row) return { group, name: row[1], designBlock: row[2] };
  }
  return null;
}

// ── THE FIELD LEDGER ────────────────────────────────────────────────────────
// Where every field of the response is shown. Groups: summary (banner / sheet summary),
// row (compact criterion row), drawer.observation / .source / .history / .routing, logic
// (validation / identity, kept for front-end logic, also listed in the drawer where useful).
export const LEDGER = {
  top: {
    success: 'logic — decode validation (lib/mackCalculatorEvidence.ts); Response details',
    schema: 'logic — decode validation; Response details',
    formula_execution: 'summary — banner chip detail (flag in words); decode validation',
    source_fetches: 'logic — decode validation (must be 0); Response details',
    writer_entries: 'logic — decode validation (must be 0); Response details',
    scores_enabled: 'summary — banner chip (Downstream calculations disabled/enabled)',
    generated_at: 'summary — banner (projection read, age); Response details',
    slots: 'row + drawer — one row per slot, 84',
    sheets: 'summary — the sheet selector order',
    families: 'row — criterion order within each table',
    counts: 'summary — banner (84 criteria, Formula Pass 2 pending of total); Response details',
    T1_installation: 'summary — banner (source-contract verification state); Response details',
    history_route: 'logic — Response details (the History call keeps the desktop\'s existing route)',
    inputs_reuse_decisions: 'drawer — Response details (full list)',
    pulse_lifecycle: 'drawer — Response details (full list)',
    activation_state: 'summary — banner chip detail (code shown as itself)',
  },
  slot: {
    field_id: 'drawer.routing; logic identity', sheet: 'summary — sheet grouping; drawer.routing',
    criterion_key: 'logic — design-row match; drawer.routing', label: 'drawer.routing (backend label; the row shows the design name)',
    block: 'row Block; block cards; drawer.routing', evidence: 'row Latest evidence / state / reference / updated / history; drawer observations',
    supporting_evidence: 'drawer observations (supporting)', calendar_evidence: 'drawer observations (release evidence); sheet summary next catalyst',
    evidence_ids: 'drawer.routing', supporting_evidence_ids: 'drawer.routing', upstream_formula_ids: 'drawer.routing',
    blocking_evidence_ids: 'block card primary blocker; drawer.routing (Formula Pass 2 blocker)', unavailable_evidence: 'drawer.routing',
    calendar_bindings: 'drawer.routing (Calendar context)', score: 'row Score (— while null)',
    formula_state: 'row state when it blocks; banner/summary readiness counts; drawer.routing', classification: 'drawer.routing',
    upstream_formula_states: 'drawer.routing (formula dependency)', workbook_destination: 'right panel; drawer.routing (formula dependency)',
    downstream: 'right panel (What this sheet routes to); drawer.routing', evidence_scope_note: 'drawer.routing',
    evidence_binding_note: 'drawer.routing', BLOC_state: 'drawer.routing',
  },
  evidence: {
    observation: ['label', 'value', 'unit', 'state', 'current_eligible', 'reference_period', 'observed_at', 'effective_at',
      'publication_at', 'source_edition_at', 'scheduled_at', 'retrieved_at', 'provider_update_at', 'provider_update_state',
      'observation_type', 'reason', 'withholding_reasons', 'raw_record', 'raw_records', 'score'],
    // freshness_evidence is the SOURCE's lifecycle (its release policy, cadence, last attempt, next watch).
    source: ['publisher', 'source_url', 'source_urls', 'origin', 'upstream_state', 'publication_state', 'frequency',
      'series_id', 'field_id', 'record_kind', 'freshness_evidence', 'compatibility_checks'],
    history: ['history_count', 'history_origin', 'history_state'],
    routing: ['binding_decisions', 'constituent_ids', 'raw_field_ids', 'constituent_coverage', 'instrument_map',
      'instrument_coverage', 'aggregation_state'],
  },
};
export const SLOT_ROUTING_KEYS = ['field_id', 'sheet', 'criterion_key', 'label', 'block', 'classification', 'formula_state',
  'score', 'evidence_ids', 'supporting_evidence_ids', 'upstream_formula_ids', 'blocking_evidence_ids', 'unavailable_evidence',
  'upstream_formula_states', 'calendar_bindings', 'workbook_destination', 'downstream', 'evidence_scope_note',
  'evidence_binding_note', 'BLOC_state'];
const SLOT_OBSERVATION_LISTS = ['evidence', 'supporting_evidence', 'calendar_evidence'];
const EVIDENCE_NAMED = new Set(Object.values(LEDGER.evidence).flat());
const SLOT_NAMED = new Set([...SLOT_ROUTING_KEYS, ...SLOT_OBSERVATION_LISTS]);
const TOP_NAMED = new Set(Object.keys(LEDGER.top));

export function otherFields(obj, level) {
  if (!obj || typeof obj !== 'object') return [];
  const named = level === 'evidence' ? EVIDENCE_NAMED : level === 'slot' ? SLOT_NAMED : TOP_NAMED;
  return Object.keys(obj).filter((k) => !named.has(k));
}

// ── KEY/VALUE LINES (JSON shown exactly, as readable lines — never a raw block, never shortened) ──
export function kvLines(value, prefix) {
  const out = [];
  const walk = (v, path) => {
    if (v !== null && typeof v === 'object') {
      const entries = Array.isArray(v) ? v.map((x, i) => [`${path}[${i}]`, x]) : Object.keys(v).map((k) => [path ? `${path}.${k}` : k, v[k]]);
      if (!entries.length) { out.push([path, Array.isArray(v) ? '[] (empty)' : '{} (empty)']); return; }
      entries.forEach(([p, x]) => walk(x, p));
      return;
    }
    out.push([path, v === null ? 'null' : v === undefined ? 'not supplied' : typeof v === 'string' ? v : String(v)]);
  };
  walk(value, prefix || '');
  return out;
}
export function groupLines(obj, keys) {
  return keys.filter((k) => own(obj, k)).flatMap((k) => kvLines(obj[k], k));
}

// ── FORMATTERS (display only) ───────────────────────────────────────────────
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
// Reference-period CODES the backend sends in place of a date, in words (same rule as STATE_LANGUAGE:
// an unknown code shows as itself). The exact code stays in the drawer.
export const REFERENCE_LANGUAGE = {
  CURRENT_SNAPSHOT_AT_RETRIEVAL: 'Snapshot at retrieval',
};
export function referenceText(ref) {
  if (ref === null || ref === undefined || ref === '') return '—';
  const s = String(ref);
  if (own(REFERENCE_LANGUAGE, s)) return REFERENCE_LANGUAGE[s];
  let m = /^(\d{4})-(\d{2})-(\d{2})(?:T[\d:.]+(?:Z|[+-]\d{2}:?\d{2})?)?$/.exec(s);
  if (m) return `${+m[3]} ${MONTHS[+m[2] - 1]} ${m[1]}`;
  m = /^(\d{4})-(\d{2})$/.exec(s);
  if (m) return `${MONTHS[+m[2] - 1]} ${m[1]}`;
  m = /^(\d{4})-?Q([1-4])$/.exec(s);
  if (m) return `Q${m[2]} ${m[1]}`;
  return s;
}
export function ageText(iso, now) {
  if (!iso) return '—';
  const t = Date.parse(String(iso));
  if (!Number.isFinite(t)) return String(iso);
  const s = Math.max(0, Math.round(((now === undefined ? Date.now() : now) - t) / 1000));
  if (s < 60) return 'just now';
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  if (s < 86400 * 60) return `${Math.floor(s / 86400)}d ago`;
  return `${Math.floor(s / (86400 * 30))}mo ago`;
}
export function untilText(iso, now) {
  const t = Date.parse(String(iso));
  if (!Number.isFinite(t)) return String(iso);
  const s = Math.round((t - (now === undefined ? Date.now() : now)) / 1000);
  if (s < 3600) return `${Math.max(1, Math.floor(s / 60))}m`;
  if (s < 86400) return `${Math.floor(s / 3600)}h`;
  return `${Math.floor(s / 86400)}d`;
}
// The value as the backend sent it (String(value), as the evidence view prints it) and its unit.
export function latestText(e) {
  if (!e) return { value: '—', unit: '' };
  if (e.value !== null && e.value !== undefined) return { value: String(e.value), unit: e.unit || '' };
  if (Array.isArray(e.raw_records) && e.raw_records.length) return { value: `${e.raw_records.length} raw records`, unit: '' };
  if (e.raw_record && typeof e.raw_record.long_percentage === 'number') {
    return { value: `Long ${e.raw_record.long_percentage}% · Short ${e.raw_record.short_percentage}%`, unit: '' };
  }
  if (Array.isArray(e.instrument_map) && e.instrument_map.length) return { value: `${e.instrument_map.length} instruments`, unit: '' };
  return { value: '—', unit: e.unit || '' };
}

// ── THE MODEL ───────────────────────────────────────────────────────────────
function distinct(list) { return list.filter((x, i) => list.indexOf(x) === i); }

export function criterionView(slot, familyOrder, now) {
  const row = designRowFor(slot.criterion_key);
  const obs = Array.isArray(slot.evidence) ? slot.evidence : [];
  const formula = slot.formula_state;
  const ready = READY_FORMULA_STATES.includes(formula);
  const waiting = WAITING_FORMULA_STATES.includes(formula);
  const all = [...obs, ...(slot.supporting_evidence || [])];
  const accessGap = all.some((e) => ACCESS_GAP_STATES.includes(e.state));
  const single = obs.length === 1 ? obs[0] : null;
  const stateLabels = distinct(obs.map((e) => stateLabel(e.state)));
  const latest = single ? latestText(single) : obs.length ? { value: `${obs.length} observations`, unit: '' } : { value: '—', unit: '' };
  return {
    id: slot.field_id,
    sheet: slot.sheet,
    key: slot.criterion_key,
    order: familyOrder.indexOf(slot.criterion_key),
    name: row ? row.name : slot.label,
    backendLabel: slot.label,
    group: row ? row.group : 'unassigned',
    block: slot.block,
    designBlock: row ? row.designBlock : null,
    formulaState: formula,
    ready, waiting, accessGap,
    observationCount: obs.length,
    latest,
    // The row's state: the formula code when it blocks (the criterion says its input is missing);
    // otherwise the observation's own state, or the distinct labels of several.
    stateText: waiting ? stateLabel(formula) : stateLabels.length ? stateLabels.join(' · ') : stateLabel(formula),
    stateCodes: waiting ? [formula] : distinct(obs.map((e) => e.state)),
    reference: single ? referenceText(single.reference_period) : obs.length ? `${obs.length} references` : '—',
    updated: single ? ageText(single.retrieved_at || single.observed_at, now) : '—',
    history: single ? String(single.history_count) : obs.length ? `${obs.reduce((t, e) => t + (Number(e.history_count) || 0), 0)} · ${obs.length} obs` : '—',
    score: slot.score,
    slot,
  };
}

export function blockerText(slot) {
  const ids = Array.isArray(slot.blocking_evidence_ids) ? slot.blocking_evidence_ids : [];
  if (!ids.length) return null;
  const e = [...(slot.evidence || []), ...(slot.supporting_evidence || [])].find((x) => x.field_id === ids[0]);
  return e ? `${e.label} · ${stateLabel(e.state)}` : ids[0];
}

export function buildModel(projection, now) {
  const slots = projection && Array.isArray(projection.slots) ? projection.slots : [];
  const sheets = projection && Array.isArray(projection.sheets) && projection.sheets.length ? projection.sheets : distinct(slots.map((s) => s.sheet));
  const families = projection && Array.isArray(projection.families) ? projection.families : distinct(slots.map((s) => s.criterion_key));
  const criteria = slots.map((s) => criterionView(s, families, now));
  const blocks = distinct([...BLOCKS, ...criteria.map((c) => c.block)]);
  const evidenceTimes = slots.flatMap((s) => (s.evidence || []).map((e) => e.retrieved_at)).filter(Boolean)
    .filter((t) => Number.isFinite(Date.parse(t)));
  const lastRefresh = evidenceTimes.length ? evidenceTimes.reduce((a, b) => (Date.parse(a) >= Date.parse(b) ? a : b)) : null;
  const counts = (projection && projection.counts) || {};
  const banner = {
    sheets: sheets.length,
    criteria: criteria.length,
    sourceContracts: projection && projection.T1_installation ? projection.T1_installation.state : null,
    formulaPending: own(counts, 'formula_pass2_pending') ? counts.formula_pass2_pending : null,
    formulaTotal: own(counts, 'slots') ? counts.slots : criteria.length,
    lastRefresh,
    lastRefreshAge: ageText(lastRefresh, now),
    generatedAt: projection ? projection.generated_at : null,
    ready: criteria.filter((c) => c.ready).length,
    waiting: criteria.filter((c) => c.waiting).length,
    accessGaps: criteria.filter((c) => c.accessGap).length,
    chip: projection ? flagLabel('scores_enabled', projection.scores_enabled) : '—',
    activation: projection ? projection.activation_state : null,
  };
  const sheetView = (sheet) => {
    const mine = criteria.filter((c) => c.sheet === sheet).sort((a, b) => a.order - b.order);
    const part = (g) => { const l = mine.filter((c) => c.group === g); return { ready: l.filter((c) => c.ready).length, total: l.length, rows: l }; };
    const blockCards = blocks.map((b) => {
      const l = mine.filter((c) => c.block === b);
      const ready = l.filter((c) => c.ready).length;
      const firstWaiting = l.find((c) => c.waiting);
      return {
        block: b, ready, total: l.length,
        state: !l.length ? 'No criteria in this block'
          : ready === l.length ? `Evidence ready · ${stateLabel('FORMULA_PASS2_PENDING')}`
          : `${l.length - ready} of ${l.length} · ${stateLabel('FORMULA_PENDING_INPUT_UNAVAILABLE')}`,
        blocker: firstWaiting ? `${firstWaiting.name} — ${blockerText(firstWaiting.slot) || stateLabel(firstWaiting.formulaState)}` : null,
      };
    }).filter((c) => c.total > 0 || BLOCKS.includes(c.block));
    // The next known catalyst: the earliest scheduled Calendar occurrence still ahead, if the response has one.
    const t0 = now === undefined ? Date.now() : now;
    const scheduled = mine.flatMap((c) => (c.slot.calendar_evidence || []).filter((e) => e.scheduled_at && Date.parse(e.scheduled_at) > t0)
      .map((e) => ({ label: e.label, at: e.scheduled_at })));
    scheduled.sort((a, b) => Date.parse(a.at) - Date.parse(b.at));
    const next = scheduled[0] || null;
    const downstream = {};
    mine.forEach((c) => Object.keys(c.slot.downstream || {}).forEach((dest) => {
      downstream[dest] = distinct([...(downstream[dest] || []), String(c.slot.downstream[dest])]);
    }));
    return {
      sheet,
      macro: part('macro'), sentiment: part('sentiment'), unassigned: part('unassigned'),
      blocks: blockCards,
      ready: mine.filter((c) => c.ready).length, waiting: mine.filter((c) => c.waiting).length,
      accessGaps: mine.filter((c) => c.accessGap).length, total: mine.length,
      nextCatalyst: next ? { text: `next ${next.label} · ${untilText(next.at, now)}`, at: next.at } : null,
      downstream,
      formulaStates: distinct(mine.map((c) => c.formulaState)),
      // Supplied only when the backend governs them; nothing in this response does.
      environment: null, horizon: null, tiers: null, bf: null,
      tiles: TILES.map((t) => ({ ...t, value: null })),
    };
  };
  return { banner, sheets, families, criteria, sheetView, topOther: otherFields(projection, 'top') };
}

// ═════════════════════════════════════════════════════════════════════════════
// PART A POLISH (owner review, 2026-10-14): a neat drawer, plain words on the first screen,
// honest banner counts, and a reader that never blanks the screen when the response moves.
// ═════════════════════════════════════════════════════════════════════════════

// Backend codes that EXPLAIN an empty or coded value, in words. Same rule as STATE_LANGUAGE: an
// unknown code shows as itself; the exact code is always kept beside it and in "All fields".
export const CODE_WORDS = {
  NOT_EXPOSED_BY_SOURCE: 'Not exposed by source',
  SOURCE_PUBLISHED: 'Published by the source',
  SOURCE_DOES_NOT_EXPOSE_UPDATE_TIMESTAMP: 'Source does not expose an update time',
  PUBLISHER_RSS_PUBLICATION_LABEL: 'Publisher RSS publication label',
  SOURCE_RELEASE_LABEL: 'Source release label',
  DATASET_EDITION_ONLY_ORIGINAL_RELEASE_UNKNOWN: 'Dataset edition only — original release unknown',
  PERMANENT_UPSTREAM_VINTAGES: 'Permanent upstream vintages',
  PERMANENT_CALENDAR_OCCURRENCES: 'Permanent Calendar occurrences',
  PERMANENT_VINTAGES: 'Permanent vintages',
  PERMANENT_RAW_RECORDS_AND_VINTAGES: 'Permanent raw records and vintages',
  NO_RETAINED_OBSERVATION: 'No retained observation',
  NO_PRODUCTION_OBSERVATION: 'No production observation',
  INPUTS_REUSED: 'Reused from Inputs',
  CALENDAR_REUSED: 'Reused from Calendar',
  CALCULATOR_DIRECT_SOURCE: 'Calculator direct source',
  TRIGGER_ONLY: 'Trigger only',
  CURRENT_SNAPSHOT_AT_RETRIEVAL: 'Snapshot at retrieval',
  OWNER_CLOSURE_T1_UPLOAD_AND_RESTART_REQUIRED: 'Owner closure: T1 upload and restart required',
  OWNER_UPLOAD_AND_COMBINED_RESTART_THEN_SEPARATE_ACQUISITION_AUTHORITY_REQUIRED:
    'Owner upload and combined restart, then separate acquisition authority required',
};
export function codeWords(code) {
  if (code === null || code === undefined || code === '') return '—';
  return own(CODE_WORDS, code) ? CODE_WORDS[code] : (own(STATE_LANGUAGE, code) ? STATE_LANGUAGE[code] : String(code));
}

// Dates read like "12 Oct 2026, 3:55 pm (Hobart)"; a date-only value stays a date ("17 Sep 2026").
// `exact` is the stored string, shown beside it in small mono.
const HOBART = 'Australia/Hobart';
export function whenText(value) {
  if (value === null || value === undefined || value === '') return { text: '—', exact: null };
  const s = String(value);
  if (/^\d{4}-\d{2}-\d{2}$/.test(s) || /^\d{4}-\d{2}$/.test(s)) return { text: referenceText(s), exact: s };
  if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(s) && Number.isFinite(Date.parse(s))) {
    try {
      const parts = new Intl.DateTimeFormat('en-AU', { timeZone: HOBART, day: 'numeric', month: 'short', year: 'numeric',
        hour: 'numeric', minute: '2-digit', hour12: true }).formatToParts(new Date(Date.parse(s)));
      const p = (t) => (parts.find((x) => x.type === t) || {}).value || '';
      return { text: `${p('day')} ${p('month')} ${p('year')}, ${p('hour')}:${p('minute')} ${p('dayPeriod').toLowerCase()} (Hobart)`, exact: s };
    } catch { return { text: s, exact: null }; }
  }
  return { text: codeWords(s), exact: own(CODE_WORDS, s) ? s : null };
}

// One named row. `text` is what reads; `exact` the stored value beside it; `href` a link.
function row(label, text, exact, href) { return { label, text: text === undefined || text === null || text === '' ? '—' : String(text), exact: exact || null, href: href || null }; }
function whenRow(label, value, emptyReasonCode) {
  if ((value === null || value === undefined || value === '') && emptyReasonCode) return row(label, codeWords(emptyReasonCode), emptyReasonCode);
  const w = whenText(value);
  return row(label, w.text, w.exact);
}
function selectorLines(series) {
  if (series === null || series === undefined) return { series: '—', selector: null };
  if (typeof series === 'string') return { series, selector: null };
  const v = series && typeof series === 'object' && typeof series.value === 'string' ? series.value : null;
  return { series: v || '—', selector: kvLines(series).map(([k, x]) => `${k}: ${x}`) };
}

// THE DRAWER for one observation, in the spec's order and words. `allFields` is every key, exactly
// as stored (folded, closed by default), so nothing is dropped.
export function observationView(e, slot) {
  const s = slot || {};
  const shownValue = latestText(e);
  const sel = selectorLines(e.series_id);
  const bindings = Array.isArray(e.binding_decisions) ? e.binding_decisions : [];
  const mine = bindings.find((b) => b.destination === s.field_id) || null;
  const calendar = (s.calendar_bindings || []).filter((b) => b.upstream_field_id === e.field_id || b.concept_id === e.field_id);
  const blocking = Array.isArray(s.blocking_evidence_ids) && s.blocking_evidence_ids.includes(e.field_id);
  const wd = s.workbook_destination || null;
  const release = (s.calendar_evidence || []).map((c) => c.label).filter(Boolean);
  const sourceRows = [
    row('Publisher', e.publisher),
    row('Source', e.source_url ? (siteName(e.source_url) || e.source_url) : null, null, e.source_url || null),
    row('Origin', codeWords(e.origin), e.origin && own(CODE_WORDS, e.origin) ? e.origin : null),
    row('Publication state', codeWords(e.publication_state), e.publication_state || null),
    row('Frequency', e.frequency),
    row('Series', sel.series),
    { ...row('Selector', sel.selector ? null : '—'), lines: sel.selector },
    row('Mack identity', e.field_id),
  ];
  (Array.isArray(e.source_urls) ? e.source_urls.filter((u) => u !== e.source_url) : []).forEach((u) => sourceRows.push(row('Source', siteName(u) || u, null, u)));
  return {
    id: e.field_id,
    title: e.label,
    value: shownValue.value === '—' ? '—' : `${readableNumber(shownValue.value, shownValue.unit)}${shownValue.unit ? ' ' + shownValue.unit : ''}`,
    state: stateLabel(e.state),
    sections: [
      { title: 'Observation', rows: [
        // An empty value says why, in the backend's own state words.
        row('Value', shownValue.value === '—' ? stateLabel(e.state) : `${readableNumber(shownValue.value, shownValue.unit)}${shownValue.unit ? ' ' + shownValue.unit : ''}`, e.value !== null && e.value !== undefined ? `stored ${e.value}` : null),
        row('State', stateLabel(e.state) + (e.reason ? ` — ${e.reason}` : ''), e.state),
        row('Reference period', referenceText(e.reference_period), e.reference_period || null),
        whenRow('Observed', e.observed_at),
        whenRow('Published', e.publication_at, e.publication_state),
        whenRow('Retrieved', e.retrieved_at),
      ] },
      { title: 'Source', rows: sourceRows },
      { title: 'History', rows: [
        row('Observations', e.history_count === undefined ? null : String(e.history_count)),
        row('Vintages', null),
        row('History state', codeWords(e.history_state), e.history_state || null),
      ], history: true },
      { title: 'Routing', rows: [
        row('Criterion', s.field_id ? `${(designRowFor(s.criterion_key) || {}).name || s.label} · ${s.sheet}` : null, s.field_id || null),
        row('Inputs identity', mine ? mine.inputs_identity : (e.origin === 'INPUTS_REUSED' ? e.field_id : null)),
        row('Calendar context', calendar.length ? calendar.map((b) => `${b.source_label} · ${codeWords(b.role)}`).join('; ') : null),
        row('Destinations', bindings.length ? distinct(bindings.map((b) => b.destination)).join(', ') : null),
        row('Formula dependency', wd ? [wd.named_block_input, wd.BF_outputs].filter(Boolean).join(' · ') : null),
        row('Release evidence', release.length ? release.join('; ') : null),
        row('Formula Pass 2 blocker', blocking ? `Yes — ${stateLabel(e.state)}` : (WAITING_FORMULA_STATES.includes(s.formula_state) ? 'No — another input blocks this criterion' : 'None')),
      ] },
    ],
    allFields: kvLines(e),
  };
}
// The criterion's own fields (everything but its observation lists), folded the same way.
export function criterionAllFields(slot) {
  return Object.keys(slot || {}).filter((k) => !SLOT_OBSERVATION_LISTS.includes(k)).flatMap((k) => kvLines(slot[k], k));
}

// Banner counts that add up: ready + waiting = total; gaps are a separate, overlapping count.
export function countsText(ready, waiting, total) { return `${ready} ready · ${waiting} waiting on upstream — of ${total}`; }
export function gapsText(gaps) { return `${gaps} with source-access gaps · gaps can overlap the two counts above`; }

// ── SOURCE LINKS (owner, 2026-10-14): a source shows as a NAME that links, never the printed address ──
// The name is the response's own (publisher / source name) where it gives one; given only a URL, the site's
// domain ("federalreserve.gov"). The exact URL stays the link's target and its hover title, and stays in
// All fields exactly as stored. Nothing is looked up.
export function siteName(url) {
  try { const u = new URL(String(url)); return /^https?:$/.test(u.protocol) ? u.hostname.replace(/^www\./i, '') : null; } catch { return null; }
}

// ── NUMBERS FOR READING (owner, 2026-10-14 second polish) ──────────────────
// First-screen values read like numbers: thousands separators; at most two decimals, never more than
// the stored value has; never an added decimal or sign; K / M / B / T only for counts and money of
// 10,000 or more (by the response's own unit). Money already stated in a scale (USD million, billion,
// thousand) is never shortened: separators only (owner, 2026-10-14). Anything that is not a plain number
// is left as stored. The exact stored value always stays in the drawer and in All fields.
export const COMPACT_UNIT = /\bpersons\b|\bcontracts\b|\bamount\b/i;
export const SCALED_UNIT = /\b(?:thousand|million|billion|trillion)s?\b/i;
const SCALES = [[1e12, 'T'], [1e9, 'B'], [1e6, 'M'], [1e3, 'K']];
export function readableNumber(value, unit) {
  const s = value === null || value === undefined ? '' : String(value).trim();
  if (!/^-?\d+(?:\.\d+)?$/.test(s)) return s;
  const n = Number(s);
  if (!Number.isFinite(n)) return s;
  const places = Math.min(2, (s.split('.')[1] || '').length);
  if (COMPACT_UNIT.test(String(unit || '')) && !SCALED_UNIT.test(String(unit || '')) && Math.abs(n) >= 10000) {
    const [size, suffix] = SCALES.find(([v]) => Math.abs(n) >= v);
    return (n / size).toLocaleString('en-US', { maximumFractionDigits: 2 }) + suffix;
  }
  return n.toLocaleString('en-US', { minimumFractionDigits: places, maximumFractionDigits: places });
}

// ── A READER THAT NEVER BLANKS THE SCREEN ───────────────────────────────────
// The desktop's strict decode (lib/mackCalculatorEvidence.ts) refuses a response that fails any of
// its checks, and the screen used to go blank. Now: the strict decode runs first; if it refuses,
// the response is still shown as received, with a notice naming every check it failed, and the 84
// expected criteria are always rendered (a criterion missing from the response says so).
export const EXPECTED = {
  schema: 'mack_calculator_evidence_projection_v1', formula_execution: false, scores_enabled: false,
  source_fetches: 0, writer_entries: 0,
};
export function readProjection(raw, strictDecode, sheets, families) {
  const notices = [];
  try { return { projection: strictDecode(raw), notices }; }
  catch (err) { notices.push(`The desktop's check refused this response (${err && err.message ? err.message : 'no reason given'}). It is shown as received.`); }
  const p = raw && typeof raw === 'object' ? { ...raw } : {};
  Object.keys(EXPECTED).forEach((k) => {
    if (!own(p, k)) notices.push(`The response has no ${k}.`);
    else if (p[k] !== EXPECTED[k]) notices.push(`${k} is ${JSON.stringify(p[k])}, expected ${JSON.stringify(EXPECTED[k])}.`);
  });
  const given = Array.isArray(p.slots) ? p.slots.filter((s) => s && typeof s === 'object') : [];
  if (!Array.isArray(p.slots)) notices.push('The response has no criteria list.');
  const byId = {};
  given.forEach((s) => { if (s.field_id && !own(byId, s.field_id)) byId[s.field_id] = s; });
  const slots = [];
  (sheets || []).forEach((sheet) => (families || []).forEach((key) => {
    const id = `mack.criterion.${sheet}.${key}`;
    const s = own(byId, id) ? byId[id] : null;
    slots.push(s ? { unavailable_evidence: [], calendar_bindings: [], upstream_formula_states: [], downstream: {}, ...s,
      evidence: Array.isArray(s.evidence) ? s.evidence : [], supporting_evidence: Array.isArray(s.supporting_evidence) ? s.supporting_evidence : [],
      calendar_evidence: Array.isArray(s.calendar_evidence) ? s.calendar_evidence : [] }
      : { field_id: id, sheet, criterion_key: key, label: key, block: (designRowFor(key) || {}).designBlock || 'Unassigned',
        evidence: [], supporting_evidence: [], calendar_evidence: [], unavailable_evidence: [], calendar_bindings: [],
        upstream_formula_states: [], downstream: {}, formula_state: 'NOT_IN_RESPONSE', score: null });
  }));
  const missing = slots.filter((s) => s.formula_state === 'NOT_IN_RESPONSE').length;
  if (missing) notices.push(`${missing} of ${slots.length} criteria are not in the response; they are listed as such.`);
  const extra = given.filter((s) => !slots.includes(s) && !slots.some((x) => x.field_id === s.field_id)).length;
  if (extra) notices.push(`${extra} criteria in the response are outside the 84 expected; they are not shown.`);
  return { projection: { ...p, slots, sheets: sheets, families: families }, notices };
}
STATE_LANGUAGE.NOT_IN_RESPONSE = 'Not in the response';
